import { IS_ANDROID_APP } from '../utils/platform';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  GraduationCap, 
  Lock, 
  ChevronRight, 
  ArrowLeft,
  Play,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import TutorialIllustration from '../components/TutorialIllustration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TutorialsPage() {
  const { techniqueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tutorials, setTutorials] = useState([]);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCandidates, setShowCandidates] = useState(true);

  const fetchTutorials = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tutorials`, { withCredentials: true });
      setTutorials(data);
    } catch (err) {
      toast.error('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTutorials();
  }, [fetchTutorials]);

  const fetchTutorialDetail = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tutorials/${id}`, { withCredentials: true });
      setCurrentTutorial(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('This tutorial requires a Premium subscription');
        navigate('/tutorials');
      } else {
        toast.error('Failed to load tutorial');
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (techniqueId) {
      fetchTutorialDetail(techniqueId);
    } else {
      setCurrentTutorial(null);
    }
  }, [techniqueId, fetchTutorialDetail]);

  const canAccessTutorial = useCallback((tutorial) => {
    if (!tutorial.locked) return true;
    return ['premium', 'premium_plus'].includes(user?.subscription_tier);
  }, [user?.subscription_tier]);

  // Tutorial Detail View
  if (techniqueId && currentTutorial) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        
        <main className="max-w-4xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/tutorials')}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6 transition-colors"
            data-testid="back-to-tutorials"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tutorials
          </button>

          <div className="mb-8">
            <span className="text-xs px-2 py-1 rounded bg-[#8B5A3C]/20 text-[#8B5A3C] font-medium">
              Level {currentTutorial.id}
            </span>
            <h1 className="font-heading text-3xl font-bold text-[var(--text-primary)] mt-2 mb-2">
              {currentTutorial.name}
            </h1>
            <p className="text-[var(--text-secondary)] text-lg">
              {currentTutorial.description}
            </p>
          </div>

          {/* Explanation */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] font-heading">How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                {currentTutorial.explanation}
              </p>
            </CardContent>
          </Card>

          {/* Steps */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] font-heading">Steps to Apply</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {currentTutorial.steps?.map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#8B5A3C]/20 text-[#8B5A3C] flex items-center justify-center text-sm font-mono flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-[var(--text-secondary)]">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* Tip */}
          {currentTutorial.tip && (
            <Card className="bg-[#8B5A3C]/5 border-[#8B5A3C]/20 mb-6">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#8B5A3C]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <GraduationCap className="w-4 h-4 text-[#8B5A3C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)] mb-1">Pro Tip</p>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{currentTutorial.tip}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Visual Illustration — the diagram of the technique */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-[var(--text-primary)] font-heading">How it looks on the board</CardTitle>
              {(currentTutorial.id === 0 || currentTutorial.id >= 2) && (
                <div className="flex items-center gap-2" data-testid="candidates-toggle-wrap">
                  <Label 
                    htmlFor="candidates-toggle" 
                    className="text-sm text-[var(--text-secondary)] cursor-pointer select-none"
                  >
                    Pencil marks
                  </Label>
                  <Switch
                    id="candidates-toggle"
                    checked={showCandidates}
                    onCheckedChange={setShowCandidates}
                    data-testid="candidates-toggle"
                  />
                </div>
              )}
            </CardHeader>
            <CardContent>
              <TutorialIllustration techniqueId={currentTutorial.id} showCandidates={showCandidates} />
            </CardContent>
          </Card>

          {/* Walkthrough example */}
          {currentTutorial.walkthrough && (
            <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading">Worked Example</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm">
                  {currentTutorial.walkthrough}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Example Grid (legacy — only shown if backend provides non-empty pattern) */}
          {currentTutorial.example_grid && currentTutorial.example_grid.flat().some((v) => v !== 0) && (
            <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading">Example Board</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="sudoku-board max-w-[300px] mx-auto">
                  {currentTutorial.example_grid.map((row, rowIndex) =>
                    row.map((cell, colIndex) => {
                      const isHighlighted = currentTutorial.highlight_cells?.some(
                        ([r, c]) => r === rowIndex && c === colIndex
                      );
                      return (
                        <div
                          key={`${rowIndex}-${colIndex}`}
                          className={`sudoku-cell ${isHighlighted ? 'pattern-highlight animate-pulse-ring' : ''}`}
                          data-testid={`example-cell-${rowIndex}-${colIndex}`}
                        >
                          {cell !== 0 && (
                            <span className="text-lg font-mono text-[var(--text-primary)]">{cell}</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Practice + Prev/Next Navigation */}
          <div className="flex flex-wrap gap-3 justify-between items-center">
            <Button
              variant="outline"
              onClick={() => navigate(`/tutorials/${Math.max(1, currentTutorial.id - 1)}`)}
              disabled={currentTutorial.id <= 1}
              className="border-[var(--border-subtle)]"
              data-testid="prev-tutorial-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Level {Math.max(1, currentTutorial.id - 1)}
            </Button>

            <Button
              onClick={() => navigate('/play')}
              className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
              data-testid="practice-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Practice Level {currentTutorial.id}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                const next = Math.min(17, currentTutorial.id + 1);
                if (!canAccessTutorial({ id: next, locked: next > 4 })) {
                  toast.info('Next tutorial requires Premium');
                  navigate('/subscription');
                  return;
                }
                navigate(`/tutorials/${next}`);
              }}
              disabled={currentTutorial.id >= 17}
              className="border-[var(--border-subtle)]"
              data-testid="next-tutorial-btn"
            >
              Level {Math.min(17, currentTutorial.id + 1)}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Tutorial List View
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Sudoku Tutorials
          </h1>
          <p className="text-[var(--text-secondary)] text-lg">
            Learn every solving technique from beginner to expert
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid gap-4">
            {tutorials.map((tutorial, index) => {
              const accessible = canAccessTutorial(tutorial);
              
              return (
                <Card 
                  key={tutorial.id}
                  className={`bg-[var(--background-surface)] border-[var(--border-subtle)] ${
                    accessible ? 'cursor-pointer hover:border-[var(--border-strong)]' : 'opacity-60'
                  } transition-all`}
                  onClick={() => accessible && navigate(`/tutorials/${tutorial.id}`)}
                  data-testid={`tutorial-${tutorial.id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded flex items-center justify-center ${
                        accessible ? 'bg-[#8B5A3C]/20' : 'bg-[var(--hover-bg-subtle)]'
                      }`}>
                        {tutorial.locked && !accessible ? (
                          <Lock className="w-5 h-5 text-[var(--text-disabled)]" />
                        ) : (
                          <GraduationCap className="w-5 h-5 text-[#8B5A3C]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[var(--text-disabled)]">
                            Level {tutorial.id}
                          </span>
                          {tutorial.locked && (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#FF9F0A]/20 text-[#FF9F0A]">
                              Premium+
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading font-semibold text-[var(--text-primary)]">
                          {tutorial.name}
                        </h3>
                        <p className="text-sm text-[var(--text-secondary)] line-clamp-1">
                          {tutorial.description}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${
                      accessible ? 'text-[var(--text-secondary)]' : 'text-[var(--text-disabled)]'
                    }`} />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upgrade CTA */}
        {!IS_ANDROID_APP && !['premium', 'premium_plus'].includes(user?.subscription_tier) && (
          <Card className="bg-gradient-to-r from-[#FF9F0A]/20 to-[#AF52DE]/20 border-[#FF9F0A]/30 mt-8">
            <CardContent className="p-6 text-center">
              <Crown className="w-10 h-10 text-[#FF9F0A] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">
                Unlock All Tutorials
              </h3>
              <p className="text-[var(--text-secondary)] mb-4 max-w-md mx-auto">
                Upgrade to Premium to access all 17 interactive tutorials with step-by-step guidance.
              </p>
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-[#FF9F0A] hover:bg-[#FFB340] text-black"
                data-testid="upgrade-tutorials"
              >
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
